import React, { useEffect, useMemo, useState } from "react";
import { Linking, ScrollView, Image, TextInput, StatusBar } from "react-native";
import {
  Box,
  MainButton,
  MainText,
  MainTitle,
  MySafeAreaView,
  PressableOpacity,
} from "../../../components/common";
import { useDispatch, useSelector } from "react-redux";
import {
  getLanguage,
  getProfileSettingsMbti,
} from "@microdoing/shared/services/profileSettings/selectors";
import {
  changeUserInfo,
  showAlert,
} from "@microdoing/shared/services/profileSettings/actions";
import i18n from "@microdoing/shared/i18n";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import { getMbtiOptions } from "./MbtiLibrary";
import WenIcon from "app/components/Icons/Search/WenIcon";
import {
  isAndroid,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import { useNavigation } from "@react-navigation/native";
import Animated, {
  interpolate,
  useAnimatedKeyboard,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import {
  modifyUser,
  updateUserWithoutResponseRequest,
} from "@microdoing/shared/services/user/actions";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import WebMbtiList from "./WebMbtiList.web";
import HeaderMbti from "./HeaderMbti";
import MbtiSearchInput from "./MbtiSearchInput";

const ReanimatedBox = Animated.createAnimatedComponent(Box);

const mbtiTestLinkFr =
  "https://www.16personalities.com/fr/test-de-personnalite";
const mbtiTestLinkEn = "https://www.16personalities.com/free-personality-test";

const Row = ({
  title,
  label,
  description,
  image,
  isSelected = false,
  onPress,
  mbti,
}) => {
  const mbtiAvatarTranslateY = useSharedValue(0);

  useEffect(() => {
    if (isSelected) {
      mbtiAvatarTranslateY.value = withTiming(-20, { duration: 200 });
      mbtiAvatarTranslateY.value = withDelay(200, withSpring(-10));
    } else {
      mbtiAvatarTranslateY.value = withSpring(0);
    }
  }, [isSelected]);

  const avatarAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateY: mbtiAvatarTranslateY.value },
        {
          rotate: `${interpolate(
            mbtiAvatarTranslateY.value,
            [0, -20],
            [0, -8],
          )}deg`,
        },
        {
          scale: interpolate(mbtiAvatarTranslateY.value, [0, -20], [1, 1.2]),
        },
      ],
    };
  }, []);

  return (
    <PressableOpacity onPress={onPress}>
      <Box
        paddingHorizontal="sm"
        backgroundColor={isSelected ? "brand.50" : "neutral.white"}
        borderRadius={normalize(8)}
        paddingVertical="s"
        justifyContent="space-between"
        flexDirection="row"
        alignItems="center"
        style={{ opacity: isSelected ? 1 : 0.5 }}
      >
        <Box flex={1}>
          <Box flexDirection="row" alignItems="center">
            <MainText color={"neutral.900"} variant="l4-bold">
              {title}
            </MainText>
            <MainText
              marginLeft="xs"
              style={{ maxWidth: "70%" }}
              color="brand.500"
              variant="l3-bold"
            >
              {label}
            </MainText>
          </Box>
          <MainText
            style={{ maxWidth: "70%" }}
            color="neutral.700"
            variant="l3-medium"
          >
            {description}
          </MainText>
        </Box>
        <Animated.Image
          source={image}
          style={[
            {
              width: normalize(66),
              height: normalize(66),
            },
            avatarAnimatedStyle,
          ]}
          resizeMode="contain"
        />
      </Box>
    </PressableOpacity>
  );
};

const MbtiSelectorScreen = () => {
  const [search, setSearch] = useState("");

  const mbti = useSelector(getProfileSettingsMbti);
  const dispatch = useDispatch();
  const language = useSelector(getLanguage);
  const navigation = useNavigation();

  const keyboard = useAnimatedKeyboard();
  const insets = useSafeAreaInsets();

  const animatedButtonStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: keyboard.height.value
            ? -keyboard.height.value + (isAndroid ? 0 : 20)
            : withTiming(0),
        },
      ],
    };
  });

  const mbtiOptions = useMemo(() => getMbtiOptions(language), [language]);

  const groupedOptions = mbtiOptions.reduce((acc, option) => {
    const category = option.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {});

  const searchUser = inputContent => {
    return mbtiOptions.filter(MTBI_OPTION =>
      MTBI_OPTION.label.toLowerCase().includes(inputContent.toLowerCase()),
    );
  };

  useEffect(() => {
    if (mbti) {
      dispatch(modifyUser({ mbti }));
      dispatch(updateUserWithoutResponseRequest({ mbti }));
    }
  }, [mbti]);

  const onChangeMbti = mbti => {
    dispatch(changeUserInfo({ mbti }));
  };

  const handleSaveMbti = () => {
    dispatch(
      showAlert({
        title: i18n.t("mbti-save-success"),
      }),
    );
    navigation.goBack();
  };

  const mbtiRows = () => {
    return searchUser(search).map(MTBI_OPTION => {
      return (
        <Box>
          <Row
            mbti={mbti}
            onPress={() => {
              onChangeMbti(MTBI_OPTION.key);
            }}
            isSelected={mbti === MTBI_OPTION.key}
            label={MTBI_OPTION.label}
            title={MTBI_OPTION.title}
            description={MTBI_OPTION.description}
            image={MTBI_OPTION.image}
          />
        </Box>
      );
    });
  };

  const mbtiCategories = () => {
    return (
      <Box
        flexWrap={isWebDesktop ? "wrap" : undefined}
        flexDirection={isWebDesktop ? "row" : "column"}
      >
        {Object.keys(groupedOptions).map(category => {
          return (
            <Box key={category} flex={1}>
              <MainText
                color="neutral.600"
                variant="l3-medium"
                marginTop="xm"
                marginBottom={isWebDesktop ? "xs" : "null"}
              >
                {category}
              </MainText>
              {groupedOptions[category].map(MTBI_OPTION => {
                return (
                  <Box style={{ marginBottom: 8 }}>
                    <Row
                      mbti={mbti}
                      onPress={() => {
                        onChangeMbti(MTBI_OPTION.key);
                      }}
                      isSelected={mbti === MTBI_OPTION.key}
                      label={MTBI_OPTION.label}
                      title={MTBI_OPTION.title}
                      description={MTBI_OPTION.description}
                      image={MTBI_OPTION.image}
                    />
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  const openMbti = () => {
    if (language === "fr") {
      Linking.openURL(mbtiTestLinkFr);
    } else {
      Linking.openURL(mbtiTestLinkEn);
    }
  };

  if (isWebDesktop)
    return (
      <WebMbtiList
        search={search}
        setSearch={setSearch}
        rows={search.length > 0 ? mbtiRows() : mbtiCategories()}
      />
    );

  return (
    <Box
      style={{ paddingTop: insets.top || theme.spacing.s }}
      flex={1}
      backgroundColor="neutral.white"
      height="100%"
    >
      <StatusBar barStyle="dark-content" />
      <Box flex={1} backgroundColor="neutral.white" height="100%">
        <Box flex={1}>
          <Box marginHorizontal="sm">
            <HeaderMbti />
          </Box>
          <Box paddingHorizontal="sm">
            <MainButton
              textVariant="l5-medium"
              color="brand.50"
              textColor="brand.500"
              onPress={openMbti}
              text={i18n.t("complete-mbti-test")}
            />
          </Box>
          <Box
            borderWidth={1}
            borderBottomWidth={0}
            borderColor="neutral.100"
            borderRadius={16}
            width="100%"
            minHeight={400}
            height="100%"
            flex={1}
          >
            <Box marginHorizontal="sm" flex={1} height="100%">
              <MbtiSearchInput
                value={search}
                setSearch={setSearch}
                placeholder={i18n.t("search-mbti-profile")}
              />
              <ScrollView
                contentContainerStyle={{
                  paddingBottom: normalize(100),
                  marginTop: -theme.spacing.xs,
                }}
                showsVerticalScrollIndicator={false}
              >
                <Box
                  style={{
                    alignSelf: "center",
                    width: "100%",
                    marginBottom: normalize(15),
                    flex: 1,
                    paddingBottom: theme.spacing.xs,
                  }}
                >
                  {search.length > 0 ? mbtiRows() : mbtiCategories()}
                </Box>
              </ScrollView>
            </Box>
          </Box>
        </Box>
        <ReanimatedBox
          position="absolute"
          bottom={!!insets.bottom ? insets.bottom : 10}
          style={animatedButtonStyle}
          width="90%"
          marginHorizontal="m"
        >
          <MainButton
            onPress={handleSaveMbti}
            margin="null"
            text={i18n.t("save-profile")}
          />
        </ReanimatedBox>
      </Box>
    </Box>
  );
};

export default MbtiSelectorScreen;
